// v10/src/components/admin/lifespan/ReplacementsHistoryModal.js
//
// Modal "Historial de reemplazos" del módulo Vida límite (A6.1) — Tarea 2.
// GET /api/aircraft-components/:id/replacements
//
// Lista cronológica (más reciente arriba) con timeline visual.
// Cada item: fecha, horómetro, nuevo S/N (si aplica), razón, quién registró.

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, History, RefreshCw, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import componentLifespanService from '../../../services/componentLifespanService';

const formatDateAR = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const formatDateTimeAR = (value, locale = 'es-AR') => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${date} ${time}`;
};

const formatHours = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 1, minimumFractionDigits: 0 });
};

const ReplacementsHistoryModal = ({ component, onClose, darkMode, locale = 'es-AR' }) => {
  const { t } = useTranslation('admin');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!component?.id) return;
      setLoading(true); setError('');
      try {
        const res = await componentLifespanService.getReplacements(component.id);
        if (cancelled) return;
        setItems(res?.data?.data || []);
      } catch (_) {
        if (!cancelled) setError(t('component_replacements_history_load_error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [component?.id, t]);

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.32)' : '#94a3b8';
  const cardBorder = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 600, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: cardBorder,
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg,#7c3aed,#6366f1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 6px 16px rgba(124,58,237,0.3)',
              }}
            >
              <History style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t('component_replacements_history_title')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted }}>
                {t('component_replacements_history_subtitle', { name: component?.name || '' })}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose}
            aria-label="close"
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              minHeight: 180, color: fgMuted, fontSize: 13,
            }}
          >
            <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
            <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
            {t('component_replacements_history_loading')}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div
            style={{
              padding: '12px 16px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.25)',
              color: '#dc2626', fontSize: 13, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 10,
            }}
          >
            <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
            {error}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && items.length === 0 && (
          <div
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 10, padding: '36px 20px',
              borderRadius: 16,
              border: darkMode ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
              background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 52, height: 52, borderRadius: 16,
                background: darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <RefreshCw style={{ width: 24, height: 24, color: '#6366f1' }} />
            </div>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: fgPrimary }}>
              {t('component_replacements_history_empty_title')}
            </p>
            <p style={{ margin: 0, fontSize: 12.5, color: fgMuted, maxWidth: 360 }}>
              {t('component_replacements_history_empty_desc')}
            </p>
          </div>
        )}

        {/* Timeline */}
        {!loading && !error && items.length > 0 && (
          <div style={{ position: 'relative', paddingLeft: 26 }}>
            {/* Línea vertical */}
            <div
              style={{
                position: 'absolute', left: 9, top: 6, bottom: 6,
                width: 2,
                background: darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
              }}
            />
            {items.map((it, idx) => (
              <div key={it.id || idx} style={{ position: 'relative', marginBottom: idx === items.length - 1 ? 0 : 16 }}>
                {/* Punto del timeline */}
                <div
                  style={{
                    position: 'absolute', left: -22, top: 8,
                    width: 14, height: 14, borderRadius: '50%',
                    background: 'linear-gradient(135deg,#7c3aed,#6366f1)',
                    border: darkMode ? '2px solid rgba(15,23,42,0.98)' : '2px solid white',
                    boxShadow: '0 2px 6px rgba(124,58,237,0.35)',
                  }}
                />
                <div
                  style={{
                    padding: '12px 14px', borderRadius: 14,
                    background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                    border: cardBorder,
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                    <strong style={{ fontSize: 14, fontWeight: 800, color: fgPrimary }}>
                      {formatDateAR(it.replacement_date)}
                    </strong>
                    <span style={{ fontSize: 12.5, color: fgMuted }}>
                      {t('component_replacements_history_at_horometer', {
                        value: formatHours(it.replacement_horometer, locale),
                      })}
                    </span>
                  </div>
                  {it.new_serial_number && (
                    <p style={{ margin: '4px 0', fontSize: 12.5, color: fgPrimary }}>
                      {t('component_replacements_history_new_serial', { value: it.new_serial_number })}
                    </p>
                  )}
                  {it.reason && (
                    <p style={{ margin: '4px 0', fontSize: 12.5, color: fgMuted, lineHeight: 1.45 }}>
                      {it.reason}
                    </p>
                  )}
                  <p style={{ margin: '6px 0 0', fontSize: 11.5, color: fgFaint }}>
                    {it.recorded_by_name
                      ? t('component_replacements_history_recorded_by', { name: it.recorded_by_name })
                      : ''}
                    {it.created_at
                      ? ` · ${t('component_replacements_history_recorded_on', { date: formatDateTimeAR(it.created_at, locale) })}`
                      : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button
            type="button" onClick={onClose}
            style={{
              padding: '10px 18px', borderRadius: 12,
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
              background: 'transparent',
              cursor: 'pointer',
              fontWeight: 600, fontSize: 13, color: fgMuted,
              fontFamily: 'inherit',
            }}
          >
            {t('component_replacements_history_btn_close')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ReplacementsHistoryModal;
